import { useState, useEffect, useCallback } from 'react';
import { Shield, AlertCircle, Info, ExternalLink, Copy, Check, Loader2, Key } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { Separator } from '@/app/components/ui/separator';
import { usePermissions } from '@/hooks/use-permissions';
import { apiClient } from '@/lib/api-client';
import type { SAMLConfiguration } from '@/lib/types';

interface SocialProvider {
  id: string;
  name: string;
  client_id: string;
}

const MODULE_OPTIONS = [
  { value: 'inventory', label: 'H3Gear' },
  { value: 'safety', label: 'H3Safe' },
  { value: 'crew', label: 'H3Crew' },
  { value: 'projects', label: 'H3Books' },
  { value: 'payroll', label: 'H3Pay' },
];

const ROLE_OPTIONS = ['admin', 'manager', 'inspector', 'viewer'];

const DEFAULT_ATTR_MAPPING: Record<string, string> = {
  email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
  first_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
  last_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
};

export function SsoSettingsPage() {
  const { canManageOrganization } = usePermissions();
  const [providers, setProviders] = useState<SocialProvider[]>([]);
  const [socialLoginEnabled, setSocialLoginEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // SAML state
  const [samlConfig, setSamlConfig] = useState<SAMLConfiguration | null>(null);
  const [samlLoading, setSamlLoading] = useState(true);
  const [samlSaving, setSamlSaving] = useState(false);
  const [samlError, setSamlError] = useState<string | null>(null);
  const [samlSuccess, setSamlSuccess] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // SAML form fields
  const [samlEnabled, setSamlEnabled] = useState(false);
  const [idpEntityId, setIdpEntityId] = useState('');
  const [idpSsoUrl, setIdpSsoUrl] = useState('');
  const [idpSloUrl, setIdpSloUrl] = useState('');
  const [idpX509Cert, setIdpX509Cert] = useState('');
  const [idpMetadataUrl, setIdpMetadataUrl] = useState('');
  const [attrEmail, setAttrEmail] = useState(DEFAULT_ATTR_MAPPING.email);
  const [attrFirstName, setAttrFirstName] = useState(DEFAULT_ATTR_MAPPING.first_name);
  const [attrLastName, setAttrLastName] = useState(DEFAULT_ATTR_MAPPING.last_name);
  const [defaultModuleRoles, setDefaultModuleRoles] = useState<Record<string, string>>({});

  const loadSamlConfig = useCallback(() => {
    setSamlLoading(true);
    apiClient
      .get<SAMLConfiguration>('/api/v1/saml-config/')
      .then((res) => {
        const cfg = res.data;
        setSamlConfig(cfg);
        setSamlEnabled(cfg.is_enabled);
        setIdpEntityId(cfg.idp_entity_id);
        setIdpSsoUrl(cfg.idp_sso_url);
        setIdpSloUrl(cfg.idp_slo_url);
        setIdpX509Cert(cfg.idp_x509_cert);
        setIdpMetadataUrl(cfg.idp_metadata_url);
        if (cfg.attribute_mapping) {
          setAttrEmail(cfg.attribute_mapping.email || DEFAULT_ATTR_MAPPING.email);
          setAttrFirstName(cfg.attribute_mapping.first_name || DEFAULT_ATTR_MAPPING.first_name);
          setAttrLastName(cfg.attribute_mapping.last_name || DEFAULT_ATTR_MAPPING.last_name);
        }
        if (cfg.default_module_roles) {
          setDefaultModuleRoles(cfg.default_module_roles);
        }
        setSamlLoading(false);
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setSamlConfig(null);
        }
        setSamlLoading(false);
      });
  }, []);

  useEffect(() => {
    apiClient
      .get<{ enabled?: boolean; providers: SocialProvider[] }>('/api/v1/auth/social/providers/')
      .then((res) => {
        setProviders(res.data.providers);
        setSocialLoginEnabled(res.data.enabled !== false);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Failed to load SSO provider configuration.');
        setIsLoading(false);
      });

    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadSamlConfig();
  }, [loadSamlConfig]);

  const handleSamlSave = async () => {
    setSamlSaving(true);
    setSamlError(null);
    setSamlSuccess(null);
    try {
      const payload = {
        is_enabled: samlEnabled,
        idp_entity_id: idpEntityId,
        idp_sso_url: idpSsoUrl,
        idp_slo_url: idpSloUrl,
        idp_x509_cert: idpX509Cert,
        idp_metadata_url: idpMetadataUrl,
        attribute_mapping: { email: attrEmail, first_name: attrFirstName, last_name: attrLastName },
        default_module_roles: Object.keys(defaultModuleRoles).length > 0 ? defaultModuleRoles : null,
      };
      const res = await apiClient.post<SAMLConfiguration>('/api/v1/saml-config/', payload);
      setSamlConfig(res.data);
      setSamlSuccess('SAML configuration saved successfully.');
    } catch {
      setSamlError('Failed to save SAML configuration.');
    } finally {
      setSamlSaving(false);
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleTestConnection = () => {
    if (!samlConfig) return;
    const loginUrl = `${apiClient.defaults.baseURL}${samlConfig.sp_metadata_url.replace('/metadata/', '/login/')}`;
    const w = 600, h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    window.open(loginUrl, 'saml_test', `width=${w},height=${h},left=${left},top=${top},popup=yes`);
  };

  if (!canManageOrganization) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Access Denied</h3>
                <p className="text-sm text-red-800 mt-1">
                  You do not have permission to manage SSO settings. Only administrators can modify these settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading || samlLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Error Loading SSO Settings</h3>
                <p className="text-sm text-red-800 mt-1">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const googleProvider = providers.find((p) => p.id === 'google');
  const microsoftProvider = providers.find((p) => p.id === 'microsoft');
  const baseUrl = apiClient.defaults.baseURL || window.location.origin;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Single Sign-On</h1>
        <p className="text-gray-600 mt-1">
          Configure OAuth providers and SAML for enterprise SSO
        </p>
      </div>

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900">How SSO Works</h3>
              <p className="text-sm text-blue-800 mt-1">
                When SSO is configured, your team will see sign-in buttons on the login page.
                For enterprise SAML, users click &quot;Sign in with SSO&quot; and enter their
                company domain to be redirected to their corporate identity provider
                (Okta, Azure AD, OneLogin, etc.). All permissions, module roles, and JSA
                signing work exactly the same.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {!socialLoginEnabled && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Social login is turned off for this platform. Google and Microsoft sign-in are
            unavailable until it is turned back on.
          </AlertDescription>
        </Alert>
      )}

      {/* Google SSO */}
      {socialLoginEnabled && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GoogleIcon />
            Google Sign-In
          </CardTitle>
          <CardDescription>
            Allow users to sign in with their Google Workspace or personal Google account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {googleProvider ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                  Configured
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Client ID: <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{googleProvider.client_id}</code>
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                  Not configured
                </span>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-border bg-gray-50 dark:bg-secondary p-4 space-y-3">
                <h4 className="font-medium text-sm text-gray-900">Setup Instructions</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                  <li>
                    Go to the{' '}
                    <a
                      href="https://console.cloud.google.com/apis/credentials"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-500 inline-flex items-center gap-1"
                    >
                      Google Cloud Console <ExternalLink className="h-3 w-3" />
                    </a>
                  </li>
                  <li>Create an OAuth 2.0 Client ID (Web application type)</li>
                  <li>
                    Add <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{window.location.origin}</code> to
                    Authorized JavaScript origins
                  </li>
                  <li>
                    Add <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">/accounts/google/login/callback/</code> to
                    Authorized redirect URIs
                  </li>
                  <li>
                    Set the environment variables:
                    <code className="block mt-1 text-xs bg-gray-100 px-2 py-1 rounded">
                      GOOGLE_OAUTH_CLIENT_ID=your-client-id
                      <br />
                      GOOGLE_OAUTH_SECRET=your-client-secret
                    </code>
                  </li>
                </ol>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Microsoft SSO */}
      {socialLoginEnabled && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MicrosoftIcon />
            Microsoft Sign-In
          </CardTitle>
          <CardDescription>
            Allow users to sign in with their Microsoft 365 or Azure AD account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {microsoftProvider ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                  Configured
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Client ID: <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{microsoftProvider.client_id}</code>
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                  Not configured
                </span>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-border bg-gray-50 dark:bg-secondary p-4 space-y-3">
                <h4 className="font-medium text-sm text-gray-900">Setup Instructions</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                  <li>
                    Go to the{' '}
                    <a
                      href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-500 inline-flex items-center gap-1"
                    >
                      Azure Portal — App Registrations <ExternalLink className="h-3 w-3" />
                    </a>
                  </li>
                  <li>Register a new application (Web platform)</li>
                  <li>
                    Set the redirect URI to{' '}
                    <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">/accounts/microsoft/login/callback/</code>
                  </li>
                  <li>Create a client secret under "Certificates &amp; secrets"</li>
                  <li>
                    Set the environment variables:
                    <code className="block mt-1 text-xs bg-gray-100 px-2 py-1 rounded">
                      MICROSOFT_OAUTH_CLIENT_ID=your-application-id
                      <br />
                      MICROSOFT_OAUTH_SECRET=your-client-secret
                    </code>
                  </li>
                </ol>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      <Separator />

      {/* SAML / Enterprise SSO */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            SAML / Enterprise SSO
          </CardTitle>
          <CardDescription>
            Configure SAML 2.0 for enterprise identity providers like Okta, Azure AD, or OneLogin
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {samlError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{samlError}</AlertDescription>
            </Alert>
          )}
          {samlSuccess && (
            <Alert className="border-green-200 bg-green-50">
              <Check className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">{samlSuccess}</AlertDescription>
            </Alert>
          )}

          {/* Enable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Enable SAML SSO</Label>
              <p className="text-sm text-gray-500">When enabled, users can sign in via your corporate identity provider</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={samlEnabled}
              onClick={() => setSamlEnabled(!samlEnabled)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                samlEnabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${samlEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          <Separator />

          {/* SP Info (read-only) */}
          {samlConfig && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
              <h4 className="font-medium text-sm text-blue-900">Service Provider Info (copy these into your IdP)</h4>
              <div className="space-y-2">
                <CopyField label="SP Metadata URL" value={`${baseUrl}${samlConfig.sp_metadata_url}`} fieldId="sp_metadata" copiedField={copiedField} onCopy={handleCopy} />
                <CopyField label="ACS URL" value={`${baseUrl}${samlConfig.acs_url}`} fieldId="acs_url" copiedField={copiedField} onCopy={handleCopy} />
                <CopyField label="Entity ID" value={samlConfig.sp_entity_id || `${baseUrl}${samlConfig.sp_metadata_url}`} fieldId="entity_id" copiedField={copiedField} onCopy={handleCopy} />
              </div>
            </div>
          )}

          {/* IdP Metadata URL */}
          <div className="space-y-2">
            <Label htmlFor="idp_metadata_url">IdP Metadata URL (optional)</Label>
            <Input id="idp_metadata_url" placeholder="https://login.microsoftonline.com/.../federationmetadata.xml" value={idpMetadataUrl} onChange={(e) => setIdpMetadataUrl(e.target.value)} />
            <p className="text-xs text-gray-500">Paste the metadata URL from your IdP to auto-populate the fields below</p>
          </div>

          <Separator />

          {/* Manual IdP Configuration */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-gray-900">Identity Provider Configuration</h4>
            <div className="space-y-2">
              <Label htmlFor="idp_entity_id">IdP Entity ID</Label>
              <Input id="idp_entity_id" placeholder="https://sts.windows.net/xxxx/" value={idpEntityId} onChange={(e) => setIdpEntityId(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="idp_sso_url">IdP SSO URL (Login Endpoint)</Label>
              <Input id="idp_sso_url" placeholder="https://login.microsoftonline.com/.../saml2" value={idpSsoUrl} onChange={(e) => setIdpSsoUrl(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="idp_slo_url">IdP SLO URL (optional, Single Logout)</Label>
              <Input id="idp_slo_url" placeholder="https://login.microsoftonline.com/.../saml2" value={idpSloUrl} onChange={(e) => setIdpSloUrl(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="idp_x509_cert">IdP X.509 Certificate</Label>
              <textarea
                id="idp_x509_cert"
                className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                placeholder="-----BEGIN CERTIFICATE-----"
                value={idpX509Cert}
                onChange={(e) => setIdpX509Cert(e.target.value)}
              />
              <p className="text-xs text-gray-500">Paste the IdP&apos;s public certificate (PEM format) for SAML signature validation</p>
            </div>
          </div>

          <Separator />

          {/* Attribute Mapping */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-gray-900">Attribute Mapping</h4>
            <p className="text-xs text-gray-500">Map your IdP&apos;s SAML attribute names to H3ight user fields</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="attr_email">Email Attribute</Label>
                <Input id="attr_email" value={attrEmail} onChange={(e) => setAttrEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="attr_first_name">First Name Attribute</Label>
                <Input id="attr_first_name" value={attrFirstName} onChange={(e) => setAttrFirstName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="attr_last_name">Last Name Attribute</Label>
                <Input id="attr_last_name" value={attrLastName} onChange={(e) => setAttrLastName(e.target.value)} />
              </div>
            </div>
          </div>

          <Separator />

          {/* Default Module Roles */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-gray-900">Default Roles for New SAML Users</h4>
            <p className="text-xs text-gray-500">When a new user signs in via SAML for the first time (without an invitation), assign these module roles automatically</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {MODULE_OPTIONS.map((mod) => (
                <div key={mod.value} className="space-y-1">
                  <Label className="text-xs">{mod.label}</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={defaultModuleRoles[mod.value] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDefaultModuleRoles((prev) => {
                        const updated = { ...prev };
                        if (val) { updated[mod.value] = val; } else { delete updated[mod.value]; }
                        return updated;
                      });
                    }}
                  >
                    <option value="">No default</option>
                    {ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button onClick={handleSamlSave} disabled={samlSaving}>
              {samlSaving ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>) : 'Save SAML Configuration'}
            </Button>
            {samlConfig && samlConfig.is_enabled && (
              <Button variant="outline" onClick={handleTestConnection}>Test Connection</Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CopyField({ label, value, fieldId, copiedField, onCopy }: {
  label: string; value: string; fieldId: string; copiedField: string | null;
  onCopy: (text: string, field: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-blue-800 w-28 shrink-0">{label}:</span>
      <code className="text-xs bg-white/70 px-2 py-1 rounded flex-1 truncate">{value}</code>
      <button type="button" onClick={() => onCopy(value, fieldId)} className="text-blue-600 hover:text-blue-500 p-1" title="Copy">
        {copiedField === fieldId ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 23 23">
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
      <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}
